import dayjs from 'dayjs';
import { FortnoxImportRun } from '../models/FortnoxImportRun';
import { FortnoxImportSummary } from '../models/FortnoxImportSummary';
import { FortnoxIntegrationType } from '../types/backendTypes/FortnoxIntegrationType';
import { DATE_TIME_FORMAT } from './dates';

export const isFortnoxImportActive = (run: FortnoxImportRun | null): boolean =>
  !!run && ['queued', 'planning', 'running'].includes(run.status);

const sumOf = (run: FortnoxImportRun, field: 'total' | 'pending' | 'failed'): number =>
  Object.values(run.progress).reduce((sum, entity) => sum + entity[field], 0);

/** How many of the run's entities are dealt with, of how many it has found so far. */
export const fortnoxImportProgress = (
  run: FortnoxImportRun
): { processed: number; total: number } => {
  const total = sumOf(run, 'total');
  return { processed: total - sumOf(run, 'pending'), total };
};

/**
 * A run can continue when its job died (stalled), when the circuit breaker stopped it with
 * entities left, or when its job never started.
 */
export const canResumeFortnoxImport = (run: FortnoxImportRun): boolean =>
  run.stalled ||
  run.status === 'failed' ||
  run.status === 'stopped' ||
  (run.status === 'partial' && sumOf(run, 'pending') > 0);

/** Failed entities can be imported again once the run has finished. */
export const canRetryFailedFortnoxImport = (run: FortnoxImportRun): boolean =>
  !isFortnoxImportActive(run) && sumOf(run, 'failed') > 0;

/** A finished run in the shape buildFortnoxImportAlert reads. Null while it is still going. */
export const fortnoxImportSummaryOf = (run: FortnoxImportRun): FortnoxImportSummary | null => {
  if ((run.status !== 'complete' && run.status !== 'partial') || !run.entities) {
    return null;
  }
  return {
    status: run.status,
    organizationId: '',
    entities: run.entities,
    failures: run.failures,
  };
};

const formatTime = (value: string | null): string =>
  value ? dayjs(value).format(DATE_TIME_FORMAT) : '';

/** One line on where the run stands, for the settings page. */
export const describeFortnoxImport = (run: FortnoxImportRun): string => {
  if (run.stalled) {
    return `Importen har stannat (senaste livstecken ${formatTime(run.heartbeatAt ?? run.createdAt)}). Den kan återupptas.`;
  }
  switch (run.status) {
    case 'queued':
      return 'Importen startar.';
    case 'planning': {
      const { processed, total } = fortnoxImportProgress(run);
      return total > 0
        ? `Hämtar listor från Fortnox: ${total} poster hittade hittills, ${processed} klara.`
        : 'Hämtar listor från Fortnox.';
    }
    case 'running': {
      const { processed, total } = fortnoxImportProgress(run);
      return `Importerar: ${processed} av ${total}.`;
    }
    case 'complete':
      return `Senaste import klar ${formatTime(run.finishedAt)}.`;
    case 'partial':
      return `Senaste import ${formatTime(run.finishedAt)} blev ofullständig.`;
    case 'failed':
      return 'Importen kunde inte startas.';
    case 'stopped': {
      const { processed, total } = fortnoxImportProgress(run);
      return `Importen stoppades ${formatTime(run.finishedAt)}. ${processed} av ${total} hann hämtas.`;
    }
  }
};

/** The phases of an import, in the order the backend runs them. */
const PHASES: Record<FortnoxIntegrationType, { key: string; label: string }[]> = {
  [FortnoxIntegrationType.Base]: [
    { key: 'customers', label: 'Kunder' },
    { key: 'articles', label: 'Artiklar' },
    { key: 'invoices', label: 'Fakturor' },
  ],
  [FortnoxIntegrationType.Salary]: [{ key: 'employees', label: 'Anställda' }],
};

export type FortnoxImportPhaseState = 'reading' | 'waiting' | 'running' | 'done' | 'unfinished';

export interface FortnoxImportPhase {
  key: string;
  label: string;
  state: FortnoxImportPhaseState;
  text: string;
}

/**
 * Where each phase of a run stands: customers, articles and invoices for Bas, employees for
 * Lön. While the lists are read the totals are not final, so every phase says so. After that
 * the first phase with anything left is the one running; the ones after it wait.
 */
export const fortnoxImportPhases = (run: FortnoxImportRun): FortnoxImportPhase[] => {
  const reading = run.status === 'queued' || run.status === 'planning';
  const working = isFortnoxImportActive(run) && !run.stalled;
  let runningFound = false;

  return PHASES[run.integrationType].map(({ key, label }) => {
    const progress = run.progress[key] ?? {
      total: 0,
      done: 0,
      skippedInactive: 0,
      failed: 0,
      pending: 0,
    };
    const processed = progress.total - progress.pending;

    if (reading) {
      return { key, label, state: 'reading', text: 'Läser in listor…' };
    }
    if (progress.pending === 0) {
      const imported = progress.done + progress.skippedInactive;
      const text =
        progress.total === 0
          ? 'Inga att hämta'
          : `Klart (${imported})${progress.failed > 0 ? `, ${progress.failed} kunde inte hämtas` : ''}`;
      return { key, label, state: 'done', text };
    }
    if (!working) {
      return {
        key,
        label,
        state: 'unfinished',
        text: `Ej klart: ${processed} av ${progress.total}`,
      };
    }
    if (!runningFound) {
      runningFound = true;
      return { key, label, state: 'running', text: `Hämtas: ${processed} av ${progress.total}` };
    }
    return { key, label, state: 'waiting', text: 'Väntar' };
  });
};

/**
 * A Bas import whose customers and articles are all in while invoices are still to come: the
 * point at which people can start working.
 */
export const fortnoxImportReadyToWork = (run: FortnoxImportRun): boolean => {
  if (run.integrationType !== FortnoxIntegrationType.Base || run.status !== 'running') {
    return false;
  }
  const pending = (key: string) => run.progress[key]?.pending ?? 0;
  return pending('customers') === 0 && pending('articles') === 0 && pending('invoices') > 0;
};
